<template>
  <NavigationMenuRoot disablePointerLeaveClose disableHoverTrigger v-model="opened" class="navigation-menu-root">
    <NavigationMenuList class="navigation-menu-list text-primary">
      <template v-for="(item, i) in menuItems" :key="i">
        <NavigationMenuItem v-if="!item.hasSubItems">
          <NavigationMenuLink asChild>
            <NuxtLink
              :to="item.link.url"
              :target="item.link.target"
              :title="t('menu.navigateTo', { label: item.link.label })"
              class="menu__link text-regular-semibold transition-colors duration-150 hover:text-blue-whale-darker hover:underline w-max block"
              :class="{ '!text-white': darkMode }"
              @click="opened = undefined"
            >
              {{ item.link.label }}
            </NuxtLink>
          </NavigationMenuLink>
        </NavigationMenuItem>

        <NavigationMenuItem v-else>
          <NavigationMenuTrigger class="menu__link" asChild>
            <div
              class="menu__link-container transition-colors duration-150 hover:text-blue-whale-darker hover:underline select-none"
              :class="[{ 'router-link-exact-active': isSubmenuActive(item) }, { '!text-white': darkMode }]"
              role="button"
            >
              <NuxtLink
                v-if="item.link.url"
                :to="item.link.url"
                :target="item.link.target"
                :title="t('menu.navigateTo', { label: item.link.label })"
                class="menu__link text-regular-semibold w-max"
                :class="{ '!text-white': darkMode }"
                tabindex="-1"
                @click.stop
                >{{ item.link.label }}</NuxtLink
              >
              <span v-else class="menu__link text-regular-semibold w-max" :class="{ '!text-white': darkMode }">{{ item.link.label }}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                class="menu__link-icon"
                aria-hidden="true"
              >
                <path
                  d="M16.293 8.29297L12 12.586L7.70697 8.29297L6.29297 9.70697L12 15.414L17.707 9.70697L16.293 8.29297Z"
                  :fill="darkMode ? '#FFFFFF' : '#37B5AA'"
                />
              </svg>
            </div>
          </NavigationMenuTrigger>
          <NavigationMenuContent class="menu__submenu absolute top-[242%]" :class="{ '!bg-primary !border-primary': darkMode }">
            <NavigationMenuSub>
              <NavigationMenuList class="menu__submenu-list">
                <NavigationMenuItem v-for="(subItem, j) in item.subItems" :key="`${i}-${j}`">
                  <NavigationMenuLink asChild>
                    <NuxtLink
                      :to="subItem.link.url"
                      :target="subItem.link.target"
                      :title="t('menu.navigateTo', { label: subItem.link.label })"
                      class="menu__link text-regular-semibold transition-colors duration-150 hover:text-blue-whale-darker hover:underline w-full block"
                      :class="{ '!text-white': darkMode }"
                      @click="opened = undefined"
                    >
                      {{ subItem.link.label }}
                    </NuxtLink>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenuSub>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </template>
    </NavigationMenuList>
  </NavigationMenuRoot>
</template>

<script setup lang="ts">
  import type { HeaderMenuItem } from './index.vue';

  import {
    NavigationMenuRoot,
    NavigationMenuList,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuTrigger,
    NavigationMenuContent,
    NavigationMenuSub,
  } from 'reka-ui';

  defineProps<{
    menuItems: HeaderMenuItem[];
    darkMode: boolean;
  }>();
  const opened = ref<string | undefined>(undefined);
  const route = useRoute();

  const { t } = useI18n();

  // Check if any submenu item is active for dropdown triggers
  const isSubmenuActive = (item: HeaderMenuItem) => {
    if (!item.hasSubItems || !item.subItems) return false;
    return item.subItems.some(subItem => route.path === subItem.link.url);
  };
</script>

<style lang="scss" scoped>
  @keyframes enter {
    from {
      opacity: 0;
      transform: translateY(-15px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes exit {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
  .navigation-menu-root {
    &:deep(.navigation-menu-list) {
      @apply flex gap-6 justify-end;
    }
    &:deep(.menu__link-container) {
      @apply cursor-pointer;
      @apply flex items-center gap-1;

      .menu__link-icon {
        @apply transition-transform duration-150;
      }

      &[data-state='open'] {
        .menu__link-icon {
          transform: rotate(180deg);
        }
      }
    }

    &:deep(.menu__submenu) {
      @apply bg-white py-4 px-6;
      @apply border border-neutral-lighter rounded-[4px];
      @apply bg-white;
      @apply transition-all duration-75 ease-in-out;

      &[data-state='open'] {
        animation: enter 75ms ease-out;
      }

      &[data-state='closed'] {
        animation: exit 75ms ease-in;
      }

      & .menu__submenu-list {
        @apply flex flex-col gap-2;
      }
    }

    &:deep(.router-link-exact-active) {
      @apply underline;
    }
  }
</style>
